// src/components/ArtistLogin.js - Enhanced Version
import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { LogIn, Eye, EyeOff, ArrowLeft, User, Lock, Sparkles } from 'lucide-react';

const API_URL = 'https://my-musing-backend.onrender.com/api';

const ArtistLogin = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/login`, formData);
      localStorage.setItem('artistToken', response.data.token);
      navigate('/artist-dashboard');
    } catch (error) {
      setError(error.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* Background Elements */}
      <div className="login-background">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <div className="floating-shape shape-3"></div>
        <div className="floating-shape shape-4"></div>
      </div>

      <div className="login-container">
        {/* Back to Home Link */}
        <a href="/" className="back-link">
          <ArrowLeft size={20} />
          <span>Back to Portfolio</span>
        </a>

        <div className="login-card">
          {/* Header Section */}
          <div className="login-header">
            <div className="logo-container">
              <img src="/logo.png" alt="My Musing Logo" className="login-logo" />
              <div className="logo-glow"></div>
            </div>
            <h2>Artist Portal</h2>
            <p>Welcome back, Debraj!</p>
            <div className="header-decoration">
              <Sparkles size={16} />
            </div>
          </div>
          
          {/* Error Display */}
          {error && (
            <div className="error-alert">
              <div className="error-icon">⚠️</div>
              <div className="error-content">
                <strong>Authentication Failed</strong>
                <p>{error}</p>
              </div>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <div className="input-wrapper">
                <div className="input-icon">
                  <User size={20} />
                </div>
                <input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="debuchat08@gmail.com"
                  disabled={loading}
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <div className="input-icon">
                  <Lock size={20} />
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter your password"
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="login-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="loading-spinner"></div>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <LogIn size={20} />
                  <span>Access Dashboard</span>
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="login-footer">
            <p>Secure artist management portal</p>
            <div className="security-badges">
              <span className="security-badge">🔒 SSL Encrypted</span>
              <span className="security-badge">🛡️ Secure Login</span>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .login-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-light) 50%, var(--color-primary) 100%);
          position: relative;
          overflow: hidden;
        }

        .login-background {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
        }

        .floating-shape {
          position: absolute;
          border-radius: 50%;
          background: linear-gradient(135deg, rgba(201, 212, 0, 0.1), rgba(212, 201, 0, 0.05));
          animation: float 6s ease-in-out infinite;
        }

        .shape-1 {
          width: 200px;
          height: 200px;
          top: 10%;
          left: 10%;
          animation-delay: 0s;
        }

        .shape-2 {
          width: 150px;
          height: 150px;
          top: 70%;
          right: 15%;
          animation-delay: 2s;
        }

        .shape-3 {
          width: 100px;
          height: 100px;
          top: 20%;
          right: 30%;
          animation-delay: 4s;
        }

        .shape-4 {
          width: 120px;
          height: 120px;
          bottom: 20%;
          left: 20%;
          animation-delay: 1s;
        }

        .login-container {
          width: 100%;
          max-width: 480px;
          padding: var(--space-6);
          position: relative;
          z-index: 1;
        }

        .back-link {
          display: inline-flex;
          align-items: center;
          gap: var(--space-2);
          color: rgba(255, 255, 255, 0.8);
          text-decoration: none;
          font-weight: var(--weight-medium);
          margin-bottom: var(--space-8);
          transition: var(--transition-normal);
          font-size: var(--text-sm);
        }

        .back-link:hover {
          color: var(--color-white);
          transform: translateX(-2px);
        }

        .login-card {
          background: var(--color-white);
          border-radius: var(--border-radius-2xl);
          padding: var(--space-10);
          box-shadow: var(--shadow-2xl);
          border: 1px solid rgba(255, 255, 255, 0.1);
          animation: slideUp 0.6s ease-out;
          position: relative;
          overflow: hidden;
        }

        .login-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, var(--color-accent), var(--color-secondary));
        }

        .login-header {
          text-align: center;
          margin-bottom: var(--space-10);
          position: relative;
        }

        .logo-container {
          position: relative;
          display: inline-block;
          margin-bottom: var(--space-6);
        }

        .login-logo {
          width: 80px;
          height: 80px;
          object-fit: contain;
          filter: drop-shadow(0 4px 12px rgba(187, 212, 0, 0.3));
          transition: var(--transition-normal);
        }

        .login-logo:hover {
          transform: scale(1.05);
          filter: drop-shadow(0 6px 16px rgba(205, 212, 0, 0.4));
        }

        .logo-glow {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 100px;
          height: 100px;
          background: radial-gradient(circle, rgba(212, 205, 0, 0.2) 0%, transparent 70%);
          border-radius: 50%;
          animation: pulse 2s ease-in-out infinite;
        }

        .login-header h2 {
          font-size: var(--text-3xl);
          font-weight: var(--weight-black);
          margin: 0 0 var(--space-3) 0;
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .login-header p {
          color: var(--color-gray-600);
          font-size: var(--text-lg);
          margin: 0;
          font-weight: var(--weight-medium);
        }

        .header-decoration {
          position: absolute;
          top: -10px;
          right: -10px;
          color: var(--color-accent);
          opacity: 0.6;
          animation: twinkle 2s ease-in-out infinite;
        }

        .error-alert {
          display: flex;
          gap: var(--space-4);
          background: linear-gradient(135deg, #fef2f2 0%, #fecaca 100%);
          border: 1px solid #fca5a5;
          border-radius: var(--border-radius-xl);
          padding: var(--space-5);
          margin-bottom: var(--space-8);
          animation: slideDown 0.3s ease-out;
        }

        .error-icon {
          font-size: var(--text-xl);
          flex-shrink: 0;
        }

        .error-content strong {
          color: #b91c1c;
          font-weight: var(--weight-semibold);
          display: block;
          margin-bottom: var(--space-1);
        }

        .error-content p {
          color: #dc2626;
          margin: 0;
          font-size: var(--text-sm);
        }

        .login-form {
          margin-bottom: var(--space-8);
        }

        .form-group {
          margin-bottom: var(--space-6);
        }

        .form-group label {
          display: block;
          margin-bottom: var(--space-2);
          font-weight: var(--weight-semibold);
          color: var(--color-primary);
          font-size: var(--text-sm);
          letter-spacing: 0.025em;
        }

        .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-icon {
          position: absolute;
          left: var(--space-4);
          z-index: 1;
          color: var(--color-gray-400);
          transition: var(--transition-normal);
        }

        .input-wrapper:focus-within .input-icon {
          color: var(--color-accent);
        }

        .input-wrapper input {
          width: 100%;
          padding: var(--space-4) var(--space-4) var(--space-4) calc(var(--space-4) + 40px);
          border: 2px solid var(--color-gray-200);
          border-radius: var(--border-radius-xl);
          font-size: var(--text-base);
          font-family: inherit;
          transition: var(--transition-normal);
          background: var(--color-white);
          color: var(--color-gray-800);
        }

        .input-wrapper input:focus {
          outline: none;
          border-color: var(--color-accent);
          box-shadow: 0 0 0 4px rgba(212, 212, 0, 0.1);
        }

        .input-wrapper input:disabled {
          background: var(--color-gray-50);
          cursor: not-allowed;
          opacity: 0.7;
        }

        .password-toggle {
          position: absolute;
          right: var(--space-4);
          background: none;
          border: none;
          color: var(--color-gray-400);
          cursor: pointer;
          padding: var(--space-2);
          border-radius: var(--border-radius);
          transition: var(--transition-normal);
        }

        .password-toggle:hover:not(:disabled) {
          color: var(--color-accent);
          background: rgba(0, 212, 170, 0.1);
        }

        .password-toggle:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .login-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: var(--space-3);
          padding: var(--space-5) var(--space-8);
          background: linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-dark) 100%);
          color: var(--color-white);
          border: none;
          border-radius: var(--border-radius-xl);
          font-weight: var(--weight-semibold);
          font-size: var(--text-lg);
          cursor: pointer;
          transition: var(--transition-normal);
          box-shadow: var(--shadow-accent);
          position: relative;
          overflow: hidden;
        }

        .login-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transition: var(--transition-slow);
        }

        .login-btn:hover:not(:disabled)::before {
          left: 100%;
        }

        .login-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: var(--shadow-glow);
        }

        .login-btn:active:not(:disabled) {
          transform: translateY(-1px);
        }

        .login-btn:disabled {
          cursor: not-allowed;
          opacity: 0.8;
          transform: none;
        }

        .loading-spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: var(--color-white);
          animation: spin 1s linear infinite;
        }

        .login-footer {
          text-align: center;
          border-top: 1px solid var(--color-gray-100);
          padding-top: var(--space-6);
        }

        .login-footer p {
          color: var(--color-gray-500);
          font-size: var(--text-sm);
          margin: 0 0 var(--space-4) 0;
        }

        .security-badges {
          display: flex;
          justify-content: center;
          gap: var(--space-4);
          flex-wrap: wrap;
        }

        .security-badge {
          display: inline-flex;
          align-items: center;
          gap: var(--space-1);
          font-size: var(--text-xs);
          color: var(--color-gray-500);
          background: var(--color-gray-50);
          padding: var(--space-1) var(--space-3);
          border-radius: var(--border-radius-full);
          border: 1px solid var(--color-gray-200);
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(10deg);
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 0.5;
            transform: translate(-50%, -50%) scale(1);
          }
          50% {
            opacity: 0.8;
            transform: translate(-50%, -50%) scale(1.1);
          }
        }

        @keyframes twinkle {
          0%, 100% {
            opacity: 0.6;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.2);
          }
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 640px) {
          .login-container {
            padding: var(--space-4);
          }

          .login-card {
            padding: var(--space-6);
          }

          .login-header h2 {
            font-size: var(--text-2xl);
          }

          .security-badges {
            flex-direction: column;
            align-items: center;
          }

          .floating-shape {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default ArtistLogin;